import { cn } from '@heidi/utils';

import { useDesignSystemIntl } from '../hooks/use-design-system-intl';

export interface CircularProgressProps {
  /**
   * Progress value from 0 to 100
   */
  progress: number;
  /**
   * Size variant: 'sm' (16px), 'md' (24px), 'lg' (40px)
   * @default 'sm'
   */
  size?: 'sm' | 'md' | 'lg';
  /**
   * Visual variant:
   * - 'default': semantic colors (stroke-outline bg, stroke-text-secondary progress)
   * - 'overlay': white strokes for use on dark/image backgrounds
   * @default 'default'
   */
  variant?: 'default' | 'overlay';
  /**
   * Accessible label describing the progress
   */
  'aria-label'?: string;
  /**
   * Additional className for the SVG element
   */
  className?: string;
}

const SIZE_CONFIG = {
  sm: { size: 16, strokeWidth: 2, radius: 6 },
  md: { size: 24, strokeWidth: 2.5, radius: 9 },
  lg: { size: 40, strokeWidth: 3, radius: 18.5 },
} as const;

/**
 * A circular progress indicator component.
 * Used for showing upload/loading progress in file cards and image thumbnails.
 */
export function CircularProgress({
  progress,
  size = 'sm',
  variant = 'default',
  'aria-label': ariaLabel,
  className,
}: CircularProgressProps) {
  const progressPercent = Math.min(100, Math.max(0, progress));
  const config = SIZE_CONFIG[size];
  const circumference = 2 * Math.PI * config.radius;
  const strokeDashoffset = circumference - (progressPercent / 100) * circumference;

  const intl = useDesignSystemIntl();
  const defaultAriaLabel = intl.formatMessage(
    {
      defaultMessage: 'Loading {percent}%',
      id: 'cE4Ir2kVPI',
      description: 'Accessible label for the circular progress indicator',
    },
    { percent: Math.round(progressPercent) }
  );

  return (
    <svg
      className={cn('-rotate-90', className)}
      width={config.size}
      height={config.size}
      viewBox={`0 0 ${config.size} ${config.size}`}
      role="img"
      aria-label={ariaLabel ?? defaultAriaLabel}
      data-testid="circular-progress"
    >
      {/* Background circle */}
      <circle
        cx={config.size / 2}
        cy={config.size / 2}
        r={config.radius}
        fill="none"
        strokeWidth={config.strokeWidth}
        className={cn(variant === 'default' ? 'stroke-outline' : 'stroke-white/30')}
      />
      {/* Progress circle */}
      <circle
        cx={config.size / 2}
        cy={config.size / 2}
        r={config.radius}
        fill="none"
        strokeWidth={config.strokeWidth}
        strokeLinecap="round"
        className={cn(variant === 'default' ? 'stroke-text-secondary' : 'stroke-white')}
        style={{
          strokeDasharray: circumference,
          strokeDashoffset,
          transition: 'stroke-dashoffset 0.1s ease-out',
        }}
      />
    </svg>
  );
}
